import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "open", label: "Open" },
  { value: "locked", label: "Locked" },
  { value: "live", label: "Live" },
  { value: "finalised", label: "Finalised" },
];

const GameweekForm = ({
  initialData = null,
  tournamentId,
  onSubmit,
  onCancel,
  existingGameweekNumbers = [],
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    number: "",
    name: "",
    status: "upcoming",
    budgetInitial: "",
    startsAt: null,
    endsAt: null,
    deadlineAt: null,
    isDoubleGameweek: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        number: initialData.number || "",
        name: initialData.name || "",
        status: initialData.status || "upcoming",
        budgetInitial: initialData.budgetInitial ?? "",
        startsAt: initialData.startsAt ? new Date(initialData.startsAt) : null,
        endsAt: initialData.endsAt ? new Date(initialData.endsAt) : null,
        deadlineAt: initialData.deadlineAt ? new Date(initialData.deadlineAt) : null,
        isDoubleGameweek: initialData.isDoubleGameweek || false,
      });
    }
  }, [initialData]);

  const validate = () => {
    const newErrors = {};

    if (!isEdit) {
      if (!formData.number || formData.number < 1) {
        newErrors.number = "Gameweek number is required and must be at least 1";
      } else if (existingGameweekNumbers.includes(parseInt(formData.number))) {
        newErrors.number = "Gameweek number already exists for this tournament";
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (
      formData.startsAt &&
      formData.endsAt &&
      formData.startsAt > formData.endsAt
    ) {
      newErrors.endsAt = "End date must be after start date";
    }

    if (
      formData.deadlineAt &&
      formData.startsAt &&
      formData.deadlineAt > formData.startsAt
    ) {
      newErrors.deadlineAt = "Deadline should be before or at start time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const payload = {
        ...formData,
        name: formData.name,
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
        tournamentCalendarId: tournamentId,
      };
      if (!isEdit) {
        payload.number = parseInt(formData.number);
      }
      onSubmit(payload);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {!isEdit && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gameweek Number *"
                type="number"
                value={formData.number}
                onChange={handleChange("number")}
                error={!!errors.number}
                helperText={errors.number || "Unique number for this gameweek"}
                disabled={isEdit}
                inputProps={{ min: 1 }}
              />
            </Grid>
          )}

          <Grid item xs={12} md={isEdit ? 12 : 6}>
            <TextField
              fullWidth
              label="Initial Budget"
              type="number"
              value={formData.budgetInitial}
              onChange={handleChange("budgetInitial")}
              helperText="Team budget for this gameweek (e.g. 100000000)"
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gameweek Name *"
              value={formData.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name || 'e.g., "Gameweek 1 - Opening Weekend"'}
              placeholder="Enter gameweek name"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>Status *</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange("status")}
                label="Status *"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDoubleGameweek}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isDoubleGameweek: e.target.checked,
                    }))
                  }
                />
              }
              label="Double Gameweek"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Starts At"
              value={formData.startsAt}
              onChange={handleDateChange("startsAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.startsAt}
                  helperText={errors.startsAt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Ends At"
              value={formData.endsAt}
              onChange={handleDateChange("endsAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.endsAt}
                  helperText={errors.endsAt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Deadline At (Transfer Deadline)"
              value={formData.deadlineAt}
              onChange={handleDateChange("deadlineAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.deadlineAt}
                  helperText={errors.deadlineAt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Status Guide:</strong>
                <br />• <strong>Upcoming:</strong> Gameweek is scheduled but not
                yet open
                <br />• <strong>Open:</strong> Users can make transfers and
                changes
                <br />• <strong>Locked:</strong> Transfers are closed, matches
                are about to start
                <br />• <strong>Live:</strong> Matches are in progress
                <br />• <strong>Finalised:</strong> All matches finished, points
                calculated
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
              >
                {isEdit ? "Save Changes" : "Create Gameweek"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default GameweekForm;
